import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

// Import our pages
import ScreensPageIntegrated from './features/screens/pages/ScreensPageIntegrated';
import PromotionsPage from './features/promotions/pages/PromotionsPage';
import DisplayPage from './features/display/pages/DisplayPage';
import TelevisaoDisplayPage from './features/display/pages/TelevisaoDisplayPage';
import StyleEditorPage from './features/style-editor/pages/StyleEditorPage';
import VideoUploadPage from './features/video-upload/pages/VideoUploadPage';
import TelevisaoPage from './features/televisao/pages/TelevisaoPage';
import TelevisoesManagementPage from './features/televisao/pages/TelevisoesManagementPage';
import LoginPage from './features/auth/pages/LoginPage';
import ProtectedRoute from './features/auth/components/ProtectedRoute';
import LandingPage from './features/landing/pages/LandingPage';
import TopMenu from './shared/components/TopMenu';
import { useDeviceDetection } from './shared/hooks/useDeviceDetection';
import { useDisplayForcedDarkLock } from './shared/hooks/useDisplayForcedDarkLock';
import { authService } from './features/auth/services/authService';

import { totemModeService } from './shared/services/totemModeService';

/** Remove barra final (exceto raiz) para comparar rotas de forma estável */
function normalizePathname(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

/**
 * No modo totem só é permitido:
 * - /televisao (fluxo de pareamento)
 * - /login (login válido na TV sem sair do contexto do totem)
 * - /display/:idTela (uma tela; não lista nem sequência)
 * - /display/televisao/:idTelevisao
 */
function isTotemAllowedPath(pathname: string): boolean {
  const p = normalizePathname(pathname);
  if (p === '/televisao' || p === '/login') {
    return true;
  }

  const tvMatch = /^\/display\/televisao\/([^/]+)$/.exec(p);
  if (tvMatch) {
    const id = tvMatch[1];
    return id.length > 0 && id !== '.' && id !== '..';
  }

  const screenMatch = /^\/display\/([^/]+)$/.exec(p);
  if (screenMatch) {
    const id = screenMatch[1];
    if (!id || id === '.' || id === '..') {
      return false;
    }
    // /display/televisao e /display/sequencia caem em /display/:id no router — bloquear no totem
    if (id === 'televisao' || id === 'sequencia') {
      return false;
    }
    return true;
  }

  return false;
}

// Create a wrapper component to use hooks
const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const lastValidTotemPathRef = useRef<string>('/televisao');
  const isDisplayPage = location.pathname.startsWith('/display');
  const isDisplaySelectionPage = /^\/display\/?$/.test(location.pathname);
  const isDisplayFullscreenPage = isDisplayPage && !isDisplaySelectionPage;
  const isStylesPage = normalizePathname(location.pathname) === '/styles';
  const isTelevisaoPage = location.pathname === '/televisao';
  const isLoginPage = location.pathname === '/login';
  const isLandingPage = location.pathname === '/';
  const isTotemMode = totemModeService.isTotemMode();
  
  // Detecção de dispositivo
  const { deviceInfo, tvZoom, isTVZoomLoading } = useDeviceDetection();

  // Visualização de qualquer tela/TV + editor de estilos: bloqueia dark forçado.
  // Ao sair, desbloqueia e restaura o dark da extensão (se o usuário usa).
  useDisplayForcedDarkLock(isDisplayFullscreenPage || isStylesPage);

  // Flag para controlar redirecionamento (sessionStorage)
  const TV_REDIRECT_KEY = 'tv_ja_redirecionou';
  const prevIsAuthenticated = useRef<boolean>(authService.isAuthenticated());
  
  // Resetar flag quando fizer login ou logout
  useEffect(() => {
    const currentIsAuthenticated = authService.isAuthenticated();
    if (prevIsAuthenticated.current !== currentIsAuthenticated) {
      sessionStorage.removeItem(TV_REDIRECT_KEY);
    }
    prevIsAuthenticated.current = currentIsAuthenticated;
  }, [location.pathname]);

  // Redirecionar UMA VEZ quando detectar TV (apenas na rota raiz)
  // NÃO redireciona se estiver em /login, /screens ou se estiver autenticado
  useEffect(() => {
    if (!deviceInfo.isTV) {
      return;
    }
    
    // Verificar se está logado - se estiver, NUNCA redireciona
    if (authService.isAuthenticated()) {
      return;
    }
    
    // Verificar se já redirecionou nesta sessão
    if (sessionStorage.getItem(TV_REDIRECT_KEY) === 'true') {
      return;
    }
    
    // Verificar rota atual usando location.pathname (mais confiável)
    const currentPath = location.pathname;
    const isRootRoute = currentPath === '/';
    const isOnTVPage = currentPath === '/televisao';
    const isOnLoginPage = currentPath === '/login';
    const isOnScreensPage = currentPath === '/screens';
    const isOnDisplayPage = currentPath.startsWith('/display');
    
    // NÃO redirecionar se estiver em /login, /screens ou /display (páginas independentes)
    // Redirecionar apenas se estiver na rota raiz
    if (isRootRoute && !isOnTVPage && !isOnDisplayPage && !isOnLoginPage && !isOnScreensPage) {
      sessionStorage.setItem(TV_REDIRECT_KEY, 'true');
      navigate('/televisao', { replace: true });
    }
  }, [deviceInfo.isTV, location.pathname, navigate]); // Incluir location.pathname para verificar rota atual

  // Proteção forte do modo totem:
  // Permitir apenas /televisao, /login, /display/:id e /display/televisao/:id (sem listagem /display ou /display/televisao).
  // Qualquer URL digitada manualmente redireciona para a última rota válida do totem.
  // Em /login não atualiza lastValidTotemPathRef — assim, após tentar um caminho inválido, volta ao display (não fica preso no login).
  useEffect(() => {
    if (!isTotemMode) {
      return;
    }

    const isAllowedRoute = isTotemAllowedPath(location.pathname);

    if (isAllowedRoute) {
      if (normalizePathname(location.pathname) !== '/login') {
        lastValidTotemPathRef.current = location.pathname + location.search;
      }
      return;
    }

    navigate(lastValidTotemPathRef.current || '/televisao', { replace: true });
  }, [location.pathname, location.search, isTotemMode, navigate]);

  // Aplicar zoom dinâmico para toda a aplicação quando for TV
  // Usar zoom CSS ao invés de transform scale para evitar cortes
  // NÃO aplicar zoom nas páginas de visualização (tela e televisão)
  const shouldApplyZoom = deviceInfo.isTV && !isDisplayPage;
  
  const appStyle = shouldApplyZoom ? {
    zoom: tvZoom,
    // Garantir que o container ocupe toda a viewport
    width: '100%',
    height: '100%',
    overflow: 'hidden'
  } : undefined;

  useEffect(() => {
    document.documentElement.classList.toggle('display-fullbleed', isDisplayFullscreenPage);

    return () => {
      document.documentElement.classList.remove('display-fullbleed');
    };
  }, [isDisplayFullscreenPage]);

  const displayStandalone = isDisplayPage;

  return (
    <div
      className={
        isDisplayFullscreenPage
          ? 'min-h-screen min-h-[100dvh] w-full min-w-0 overflow-hidden bg-black'
          : displayStandalone
            ? 'min-h-screen min-h-[100dvh] w-full min-w-0 bg-gray-50'
          : 'min-h-screen bg-gray-50'
      }
      style={appStyle}
    >
      {/* Loading overlay para TV (oculta transição do zoom) - APENAS na tela de login */}
      {isTVZoomLoading && isLoginPage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#f9fafb', // bg-gray-50
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      )}
      
      {/* Navigation */}
      {!isLoginPage && !isTelevisaoPage && !isTotemMode && !isLandingPage && <TopMenu />}

      {/* Main Content */}
      <main
        className={
          isDisplayPage
            ? 'block w-full max-w-none min-h-[100dvh] min-w-0 p-0 m-0 bg-black'
            : isTotemMode || isLandingPage
              ? 'mx-auto'
              : 'max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-6'
        }
      >
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/screens"
            element={
              <ProtectedRoute>
                <ScreensPageIntegrated />
              </ProtectedRoute>
            }
          />
          <Route
            path="/promotions"
            element={
              <ProtectedRoute>
                <PromotionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/styles"
            element={
              <ProtectedRoute>
                <StyleEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/videos"
            element={
              <ProtectedRoute>
                <VideoUploadPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/display/televisao/:id"
            element={
              <ProtectedRoute>
                <TelevisaoDisplayPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/display/:id"
            element={
              <ProtectedRoute>
                <DisplayPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/display"
            element={
              <ProtectedRoute>
                <DisplayPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/televisao"
            element={<TelevisaoPage />}
          />
          <Route
            path="/televisoes"
            element={
              <ProtectedRoute>
                <TelevisoesManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              isTotemMode ? (
                <Navigate to={lastValidTotemPathRef.current || '/televisao'} replace />
              ) : authService.isAuthenticated() ? (
                <Navigate to="/screens" replace />
              ) : (
                <LandingPage />
              )
            }
          />
          <Route
            path="*"
            element={
              isTotemMode ? (
                <Navigate to={lastValidTotemPathRef.current || '/televisao'} replace />
              ) : authService.isAuthenticated() ? (
                <Navigate to="/screens" replace />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        </Routes>
      </main>
    </div>
  );
};

// Main App component
const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;